import { useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { dimensions, typographyTokens } from "@accredo/fe-shared/theme/index.js";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import { FileUploadOutlined as UploadIcon } from "@mui/icons-material";

/**
 * Drag-and-drop upload zone.
 *
 * Props:
 *   onDrop             — (files: File[]) => void  called with validated File objects
 *   accept             — string  e.g. 'image/*,.pdf'  (passed to the hidden <input>)
 *   allowedExtensions  — string[]  e.g. ['pdf', 'jpg', 'png']  (displays user-friendly extensions)
 *   multiple           — boolean  whether multiple files may be dropped at once
 *                              (single-file mode drops only the first file in the list)
 *   disabled           — boolean
 */
export default function DropZone({
  onDrop,
  accept,
  multiple = true,
  disabled = false,
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0); // tracks nested drag-enter/leave pairs

  // --- drag handlers ---

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    onDrop(multiple ? files : [files[0]]);
  };

  // --- click-to-browse ---

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) {
      onDrop(multiple ? files : [files[0]]);
    }
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  // --- logic ---

  // Display "Zulässige Dateitypen" if either is provided
  const displayExtensions = accept.length > 0 ? accept.join(", ") : "";

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Upload files — drag and drop or click to browse"
      aria-disabled={disabled}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        border: `1px dashed ${dragging ? componentTokens.uploadDropzone.border.drag : componentTokens.uploadDropzone.border.default}`,
        borderRadius: componentTokens.uploadDropzone.borderRadius,
        padding: componentTokens.uploadDropzone.padding,
        textAlign: "center",
        minHeight: "100px",
        flexGrow: 1,
        ":hover": {
          cursor: disabled ? "default" : "pointer",
          backgroundColor: disabled ? "lightgray" : "clickableHover",
        },
        backgroundColor: disabled
          ? componentTokens.uploadDropzone.background.disabled
          : dragging
            ? componentTokens.uploadDropzone.background.dragging
            : componentTokens.uploadDropzone.background.default,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box>
        <Box sx={{ mb: dimensions["md"] }}>
          <UploadIcon />
        </Box>
        <Box sx={{ mb: dimensions["md"] }}>
          Datei hier ablegen
          <br />
          {dragging ? "... " : " oder "}
        </Box>
        <DsButton
          sx={{
            mb: dimensions["lg"],
            opacity: dragging ? 0.1 : 1,
          }}
        >
          Auswählen
        </DsButton>

        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          style={{ display: "none" }}
          onChange={handleInputChange}
        />

        {displayExtensions && (
          <Typography
            component="div"
            sx={{
              fontSize: typographyTokens.size.sm,
              fontWeight: typographyTokens.weight.regular,
              color: componentTokens.uploadDropzone.text.color,
              maxWidth: "75%",
              mx: "auto",
              textWrap: "balance",
              mb: dimensions["md"],
            }}
          >
            <p>Maximale Dateigröße: 15 MB</p>
            <p>Zulässige Dateitypen: {displayExtensions}</p>
          </Typography>
        )}
      </Box>
    </Box>
  );
}
